import React, { useState, useEffect } from 'react';
import {
  Calendar, Settings as SettingsIcon, RefreshCw, ExternalLink, Clock,
  User, Mail, Video, Phone, MapPin, CheckCircle, XCircle, AlertCircle,
  Save, TestTube, Eye, EyeOff, Link, Key, MessageSquare
} from 'lucide-react';
import { calendlyService, CalendlyEvent, CalendlyConfig } from '../services/calendlyService';

const CalendlyModule: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'settings'>('upcoming');
  const [upcoming, setUpcoming] = useState<CalendlyEvent[]>([]);
  const [past, setPast] = useState<CalendlyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [config, setConfig] = useState<CalendlyConfig>({ apiKey: '', webhookUrl: '', isConfigured: false, schedulingUrl: '' });
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const savedConfig = await calendlyService.loadConfig();
      if (savedConfig) setConfig(savedConfig);

      const [up, pa] = await Promise.all([
        calendlyService.getUpcomingEvents(),
        calendlyService.getPastEvents(),
      ]);
      setUpcoming(up);
      setPast(pa);
    } catch (err) {
      console.error('Error loading Calendly data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const count = await calendlyService.syncEvents();
      await loadData();
      alert(`${count} eventos sincronizados desde Calendly`);
    } catch (err: any) {
      alert(err.message || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config.apiKey || config.apiKey.trim().length < 10) {
      alert('API Key inválida');
      return;
    }
    setSaving(true);
    try {
      await calendlyService.saveConfig(config);
      alert('Configuración guardada correctamente');
    } catch (err: any) {
      alert(err.message || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      // Temporarily set config for testing
      await calendlyService.saveConfig(config);
      await calendlyService.loadConfig();
      await calendlyService.testConnection();
      setTestResult({ success: true, message: 'Conexión exitosa con Calendly' });
    } catch (err: any) {
      setTestResult({ success: false, message: err.message || 'Error de conexión' });
    }
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' }),
      time: d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const getDuration = (start: string, end: string): string => {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const mins = Math.round(ms / 60000);
    return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
  };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const webhookUrl = `${supabaseUrl}/functions/v1/calendly-webhook`;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Calendly</h1>
        <p className="text-gray-600">Gestión de reuniones y agenda conectada con Calendly</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'upcoming' as const, label: 'Próximas', icon: Calendar },
              { id: 'past' as const, label: 'Pasadas', icon: Clock },
              { id: 'settings' as const, label: 'Configuración', icon: SettingsIcon },
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {tab.id === 'upcoming' && upcoming.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">{upcoming.length}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'settings' ? (
            <SettingsTab
              config={config}
              setConfig={setConfig}
              showApiKey={showApiKey}
              setShowApiKey={setShowApiKey}
              saving={saving}
              testResult={testResult}
              webhookUrl={webhookUrl}
              onSave={handleSaveConfig}
              onTest={handleTest}
            />
          ) : (
            <EventsTab
              events={activeTab === 'upcoming' ? upcoming : past}
              loading={loading}
              syncing={syncing}
              isUpcoming={activeTab === 'upcoming'}
              config={config}
              onSync={handleSync}
              formatDateTime={formatDateTime}
              getDuration={getDuration}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ================================================================
// Events Tab
// ================================================================
const EventsTab: React.FC<{
  events: CalendlyEvent[];
  loading: boolean;
  syncing: boolean;
  isUpcoming: boolean;
  config: CalendlyConfig;
  onSync: () => void;
  formatDateTime: (iso: string) => { date: string; time: string };
  getDuration: (start: string, end: string) => string;
}> = ({ events, loading, syncing, isUpcoming, config, onSync, formatDateTime, getDuration }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {isUpcoming ? 'Próximas Reuniones' : 'Reuniones Pasadas'}
        </h2>
        <div className="flex items-center space-x-2">
          {config.schedulingUrl && (
            <a
              href={config.schedulingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Abrir Calendly</span>
            </a>
          )}
          {config.isConfigured && (
            <button
              onClick={onSync}
              disabled={syncing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              <span>{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
            </button>
          )}
        </div>
      </div>

      {events.length > 0 ? (
        <div className="space-y-3">
          {events.map(event => {
            const start = formatDateTime(event.start_time);
            const duration = getDuration(event.start_time, event.end_time);
            const isCanceled = event.status === 'canceled';

            return (
              <div key={event.id} className={`border rounded-lg p-4 transition-colors ${
                isCanceled ? 'border-red-200 bg-red-50 opacity-60' : 'border-gray-200 hover:shadow-sm'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${isCanceled ? 'bg-red-100' : 'bg-blue-100'}`}>
                      <Calendar className={`w-5 h-5 ${isCanceled ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className={`text-sm font-medium ${isCanceled ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                          {event.event_type_name}
                        </h3>
                        {isCanceled && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Cancelada</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>{start.date} · {start.time}</span>
                        </span>
                        <span>{duration}</span>
                      </div>
                      {event.invitee_name && (
                        <div className="flex items-center space-x-3 mt-2 text-sm">
                          <span className="flex items-center space-x-1 text-gray-700">
                            <User className="w-3 h-3" />
                            <span className="font-medium">{event.invitee_name}</span>
                          </span>
                          {event.invitee_email && (
                            <span className="flex items-center space-x-1 text-gray-500">
                              <Mail className="w-3 h-3" />
                              <span>{event.invitee_email}</span>
                            </span>
                          )}
                        </div>
                      )}
                      {event.location_url && !isCanceled && (
                        <a
                          href={event.location_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1 mt-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <Video className="w-3 h-3" />
                          <span>Unirse a la reunión</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {event.cancel_reason && (
                        <p className="text-xs text-red-600 mt-1">Motivo: {event.cancel_reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-3">
                    {event.contact_id ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Vinculado</span>
                      </span>
                    ) : event.lead_id ? (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Lead</span>
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Sin vincular</span>
                    )}
                  </div>
                </div>

                {event.questions_and_answers && event.questions_and_answers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                    {event.questions_and_answers.map((qa, i) => (
                      <div key={i} className="text-xs">
                        <span className="text-gray-500">{qa.question}:</span>{' '}
                        <span className="text-gray-700">{qa.answer}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {isUpcoming ? 'No hay reuniones próximas' : 'No hay reuniones pasadas'}
          </h3>
          <p className="text-sm text-gray-500">
            {config.isConfigured
              ? 'Las reuniones agendadas via Calendly aparecerán aquí automáticamente'
              : 'Configura tu API Key de Calendly en la pestaña Configuración'}
          </p>
        </div>
      )}
    </div>
  );
};

// ================================================================
// Settings Tab
// ================================================================
const SettingsTab: React.FC<{
  config: CalendlyConfig;
  setConfig: (c: CalendlyConfig) => void;
  showApiKey: boolean;
  setShowApiKey: (v: boolean) => void;
  saving: boolean;
  testResult: { success: boolean; message: string } | null;
  webhookUrl: string;
  onSave: () => void;
  onTest: () => void;
}> = ({ config, setConfig, showApiKey, setShowApiKey, saving, testResult, webhookUrl, onSave, onTest }) => {
  const [copiedWebhook, setCopiedWebhook] = useState(false);

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Connection status */}
      <div className={`rounded-lg p-4 ${
        config.isConfigured && testResult?.success ? 'bg-green-50 border border-green-200' :
        testResult?.success === false ? 'bg-red-50 border border-red-200' :
        'bg-blue-50 border border-blue-200'
      }`}>
        <div className="flex items-center space-x-3">
          {config.isConfigured && testResult?.success ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : testResult?.success === false ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : (
            <Key className="w-5 h-5 text-blue-600" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">
              {config.isConfigured && testResult?.success ? 'Calendly Conectado' :
               testResult?.success === false ? 'Error de Conexión' :
               'Configurar Calendly'}
            </h3>
            <p className="text-sm text-gray-600">
              {testResult?.message || 'Configura tu Personal Access Token de Calendly'}
            </p>
          </div>
        </div>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Personal Access Token *</label>
        <p className="text-xs text-gray-500 mb-2">
          Obtén tu token en <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Calendly → Integrations → API & Webhooks</a>
        </p>
        <div className="relative">
          <input
            type={showApiKey ? 'text' : 'password'}
            value={config.apiKey}
            onChange={(e) => setConfig({ ...config, apiKey: e.target.value.trim() })}
            className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="eyJhbGciOi..."
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
            <button onClick={() => setShowApiKey(!showApiKey)} className="p-1 text-gray-400 hover:text-gray-600">
              {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button
              onClick={onTest}
              disabled={!config.apiKey}
              className="flex items-center space-x-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
            >
              <TestTube className="w-3 h-3" />
              <span>Test</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scheduling URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">URL de tu Calendly</label>
        <input
          type="url"
          value={config.schedulingUrl}
          onChange={(e) => setConfig({ ...config, schedulingUrl: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="https://calendly.com/tu-usuario"
        />
      </div>

      {/* Webhook URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">URL del Webhook</label>
        <p className="text-xs text-gray-500 mb-2">
          Copia esta URL y pégala en Calendly → Integrations → API & Webhooks → Create Webhook Subscription
        </p>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={webhookUrl}
            readOnly
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
          />
          <button
            onClick={copyWebhookUrl}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            {copiedWebhook ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Link className="w-4 h-4" />}
          </button>
        </div>
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-800">
            <strong>Eventos a subscribir:</strong> invitee.created, invitee.canceled
          </p>
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving || !config.apiKey}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="w-4 h-4" />}
          <span>{saving ? 'Guardando...' : 'Guardar Configuración'}</span>
        </button>
      </div>

      {/* How to setup */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Cómo configurar</h4>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>Ve a <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noopener noreferrer" className="underline">Calendly → Integrations → API & Webhooks</a></li>
          <li>Genera un Personal Access Token y pégalo arriba</li>
          <li>Copia la URL del webhook de arriba</li>
          <li>En Calendly, crea un Webhook Subscription con esa URL</li>
          <li>Selecciona los eventos: <strong>invitee.created</strong> e <strong>invitee.canceled</strong></li>
          <li>Pulsa "Test" para verificar la conexión</li>
          <li>Las reuniones aparecerán automáticamente en el CRM</li>
        </ol>
      </div>
    </div>
  );
};

export default CalendlyModule;
